import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Play, ExternalLink, FileText, Users, Mail, 
  MessageSquare, Phone, Building, Truck, ClipboardList,
  AlertTriangle, CheckCircle, Search, Send, Copy, Eye
} from 'lucide-react';
import { useGenericTimeTracking } from '../../hooks/useTimeTracking';

export default function TrainingSandbox() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('tools');
  const [selectedTool, setSelectedTool] = useState(null);
  const [practiceDialog, setPracticeDialog] = useState(false);
  const [practiceData, setPracticeData] = useState({});

  // Track time spent practicing in the Sandbox
  useGenericTimeTracking('sandbox', null, true);

  const lang = user?.training_language || 'es';
  const t = (en, es) => lang === 'es' ? es : en;

  // Customer Service Agent Tools
  const tools = [
    {
      id: 'funding-application',
      name: t('Funding Application Link', 'Link de Solicitud de Financiamiento'),
      description: t('Share this link with potential customers to start their application', 'Comparte este enlace con clientes potenciales para iniciar su solicitud'),
      icon: FileText,
      url: 'https://inspiredfunding.org/get-funding',
      color: 'bg-blue-500',
      category: 'links',
      practice: {
        title: t('Practice: Share Application Link', 'Práctica: Compartir Link de Solicitud'),
        scenario: t(
          'A customer called asking how to apply for equipment financing. Practice explaining the process and sharing the link.',
          'Un cliente llamó preguntando cómo solicitar financiamiento para equipo. Practica explicar el proceso y compartir el link.'
        ),
        steps: [
          t('Greet the customer professionally', 'Saluda al cliente profesionalmente'),
          t('Ask about their equipment needs', 'Pregunta sobre sus necesidades de equipo'),
          t('Explain the application process', 'Explica el proceso de solicitud'),
          t('Share the application link', 'Comparte el link de solicitud'),
          t('Offer to help with any questions', 'Ofrece ayuda con cualquier pregunta')
        ]
      }
    },
    {
      id: 'efai-portal',
      name: t('EFAI Partner Portal', 'Portal de Socios EFAI'),
      description: t('Access the partner portal for file submissions and tools', 'Accede al portal de socios para envío de archivos y herramientas'),
      icon: Building,
      url: 'https://partner.inspiredfunding.org/live-file-submission/',
      credentials: { username: 'inspiredshared', password: '20182018' },
      color: 'bg-purple-500',
      category: 'portals',
      practice: {
        title: t('Practice: EFAI Portal Login', 'Práctica: Login Portal EFAI'),
        scenario: t(
          'Learn how to access the EFAI portal to submit applications and check status.',
          'Aprende cómo acceder al portal EFAI para enviar solicitudes y verificar estatus.'
        ),
        steps: [
          t('Go to the EFAI portal URL', 'Ve al URL del portal EFAI'),
          t('Enter the shared username', 'Ingresa el nombre de usuario compartido'),
          t('Enter the password', 'Ingresa la contraseña'),
          t('Navigate to the submission section', 'Navega a la sección de envío'),
          t('Familiarize yourself with the interface', 'Familiarízate con la interfaz')
        ]
      }
    },
    {
      id: 'file-submission',
      name: t('Live File Submission', 'Envío de Archivos en Vivo'),
      description: t('Submit customer applications and documents', 'Envía solicitudes y documentos de clientes'),
      icon: Send,
      url: 'https://partner.inspiredfunding.org/live-file-submission/',
      color: 'bg-green-500',
      category: 'portals',
      practice: {
        title: t('Practice: Submit Application', 'Práctica: Enviar Solicitud'),
        scenario: t(
          'A customer has completed their application. Practice the submission process.',
          'Un cliente ha completado su solicitud. Practica el proceso de envío.'
        ),
        steps: [
          t('Verify all required documents are collected', 'Verifica que todos los documentos requeridos estén recolectados'),
          t('Log into the submission portal', 'Ingresa al portal de envío'),
          t('Upload customer documents', 'Sube los documentos del cliente'),
          t('Fill in application details', 'Completa los detalles de la solicitud'),
          t('Submit and note the confirmation number', 'Envía y anota el número de confirmación')
        ]
      }
    },
    {
      id: 'loi-pricer',
      name: t('LOI Pricer', 'LOI Pricer'),
      description: t('Generate Letter of Intent pricing for customers', 'Genera precios de Carta de Intención para clientes'),
      icon: ClipboardList,
      url: 'https://partner.inspiredfunding.org/live-file-submission/',
      color: 'bg-amber-500',
      category: 'portals',
      practice: {
        title: t('Practice: Generate LOI', 'Práctica: Generar LOI'),
        scenario: t(
          'A customer wants to know pricing before committing. Practice using the LOI pricer.',
          'Un cliente quiere saber el precio antes de comprometerse. Practica usando el LOI pricer.'
        ),
        steps: [
          t('Gather equipment details from customer', 'Reúne detalles del equipo del cliente'),
          t('Access the LOI pricer tool', 'Accede a la herramienta LOI pricer'),
          t('Enter equipment price and terms', 'Ingresa el precio del equipo y términos'),
          t('Generate the LOI quote', 'Genera la cotización LOI'),
          t('Share pricing options with customer', 'Comparte opciones de precio con el cliente')
        ]
      }
    },
    {
      id: 'spec-sheet',
      name: t('Spec Sheet Generator', 'Generador de Spec Sheet'),
      description: t('Generate spec sheets for equipment sold by private parties', 'Genera hojas de especificaciones para equipo vendido por particulares'),
      icon: FileText,
      url: 'https://partner.inspiredfunding.org/specsheet/',
      color: 'bg-cyan-500',
      category: 'portals',
      practice: {
        title: t('Practice: Create Spec Sheet', 'Práctica: Crear Spec Sheet'),
        scenario: t(
          'A customer is buying equipment from a private seller. Create a spec sheet.',
          'Un cliente está comprando equipo de un vendedor particular. Crea un spec sheet.'
        ),
        steps: [
          t('Confirm this is a private party sale', 'Confirma que es una venta de particular'),
          t('Access the spec sheet generator', 'Accede al generador de spec sheet'),
          t('Enter all equipment details', 'Ingresa todos los detalles del equipo'),
          t('Include VIN/serial numbers', 'Incluye números VIN/serie'),
          t('Generate and save the spec sheet', 'Genera y guarda el spec sheet')
        ]
      }
    },
    {
      id: 'business-plan',
      name: t('Business Plan Form', 'Formulario de Plan de Negocio'),
      description: t('Help customers complete their business plan', 'Ayuda a clientes a completar su plan de negocio'),
      icon: FileText,
      url: 'https://dochub.com/esign-platform/Noek8bMwx8keXMvwl7d2qm/general-business-plan?dt=-SUaivx5fYx4giGccC1H',
      color: 'bg-indigo-500',
      category: 'documents',
      practice: {
        title: t('Practice: Guide Business Plan', 'Práctica: Guiar Plan de Negocio'),
        scenario: t(
          'A customer needs help understanding the business plan form.',
          'Un cliente necesita ayuda para entender el formulario del plan de negocio.'
        ),
        steps: [
          t('Open the business plan form', 'Abre el formulario del plan de negocio'),
          t('Explain each section to the customer', 'Explica cada sección al cliente'),
          t('Help them provide accurate information', 'Ayúdalos a proporcionar información precisa'),
          t('Review completed sections', 'Revisa las secciones completadas'),
          t('Submit or save progress', 'Envía o guarda el progreso')
        ]
      }
    },
    {
      id: 'ban-list',
      name: t('Ban List / Prohibited Clients', 'Lista de Prohibidos / Clientes Vetados'),
      description: t('Check if a customer or agency is on the ban list', 'Verifica si un cliente o agencia está en la lista de prohibidos'),
      icon: AlertTriangle,
      url: 'https://docs.google.com/spreadsheets/u/0/d/1uZ-x1puduCJl9iUFoS5H_MFhVL-0nLLYEjU9BpKoSbM/htmlview',
      color: 'bg-red-500',
      category: 'verification',
      practice: {
        title: t('Practice: Verify Ban List', 'Práctica: Verificar Lista de Prohibidos'),
        scenario: t(
          'Before processing an application, verify the customer is not on the ban list.',
          'Antes de procesar una solicitud, verifica que el cliente no esté en la lista de prohibidos.'
        ),
        steps: [
          t('Access the ban list spreadsheet', 'Accede a la hoja de cálculo de prohibidos'),
          t('Search by customer name', 'Busca por nombre del cliente'),
          t('Search by business name', 'Busca por nombre del negocio'),
          t('Search by phone number', 'Busca por número de teléfono'),
          t('Document your verification', 'Documenta tu verificación')
        ],
        warning: t(
          'NEVER process applications from banned customers or agencies!',
          '¡NUNCA proceses solicitudes de clientes o agencias prohibidos!'
        )
      }
    },
    {
      id: 'fleet-truck',
      name: t('Fleet Truck Availability', 'Disponibilidad de Camiones de Flotilla'),
      description: t('Check availability of fleet trucks from CamionDepot', 'Verifica disponibilidad de camiones de flotilla de CamionDepot'),
      icon: Truck,
      url: 'https://partner.inspiredfunding.org/fleet-truck/',
      color: 'bg-orange-500',
      category: 'verification',
      practice: {
        title: t('Practice: Check Fleet Availability', 'Práctica: Verificar Disponibilidad de Flotilla'),
        scenario: t(
          'A customer is interested in a fleet truck. Verify its availability.',
          'Un cliente está interesado en un camión de flotilla. Verifica su disponibilidad.'
        ),
        steps: [
          t('Get the truck details from customer', 'Obtén los detalles del camión del cliente'),
          t('Access the fleet truck portal', 'Accede al portal de camiones de flotilla'),
          t('Search by VIN or stock number', 'Busca por VIN o número de stock'),
          t('Verify availability and pricing', 'Verifica disponibilidad y precio'),
          t('Provide information to customer', 'Proporciona información al cliente')
        ]
      }
    },
    {
      id: 'zohomail',
      name: 'Zoho Mail',
      description: t('Send emails to customers and communicate with credit department', 'Envía correos a clientes y comunícate con el departamento de crédito'),
      icon: Mail,
      url: null,
      color: 'bg-yellow-500',
      category: 'communication',
      practice: {
        title: t('Practice: Email Communication', 'Práctica: Comunicación por Email'),
        scenario: t(
          'Practice writing professional emails to customers and internal teams.',
          'Practica escribir correos profesionales a clientes y equipos internos.'
        ),
        steps: [
          t('Use a professional greeting', 'Usa un saludo profesional'),
          t('State the purpose clearly', 'Establece el propósito claramente'),
          t('Include all relevant information', 'Incluye toda la información relevante'),
          t('Use proper formatting', 'Usa formato apropiado'),
          t('End with a professional closing', 'Termina con un cierre profesional')
        ],
        templates: [
          { name: t('Welcome Email', 'Email de Bienvenida'), type: 'customer' },
          { name: t('Document Request', 'Solicitud de Documentos'), type: 'customer' },
          { name: t('Status Update', 'Actualización de Estatus'), type: 'customer' },
          { name: t('Credit Team Escalation', 'Escalación a Equipo de Crédito'), type: 'internal' }
        ]
      }
    },
    {
      id: 'bitrix',
      name: '360Portal.com (CRM)',
      description: t('Log notes, calls, and upload collected documents in your Inspired CRM', 'Registra notas, llamadas y sube documentos recolectados en tu CRM de Inspired'),
      icon: Users,
      url: 'https://360portal.com',
      color: 'bg-sky-500',
      category: 'crm',
      practice: {
        title: t('Practice: CRM Documentation', 'Práctica: Documentación en CRM'),
        scenario: t(
          'After each customer interaction, properly document in 360Portal.com.',
          'Después de cada interacción con cliente, documenta apropiadamente en 360Portal.com.'
        ),
        steps: [
          t('Find or create the customer record', 'Encuentra o crea el registro del cliente'),
          t('Log all call details', 'Registra todos los detalles de la llamada'),
          t('Add relevant notes', 'Agrega notas relevantes'),
          t('Upload any received documents', 'Sube cualquier documento recibido'),
          t('Update deal stage if applicable', 'Actualiza etapa del trato si aplica')
        ]
      }
    },
    {
      id: 'ringcentral',
      name: 'RingCentral SMS',
      description: t('Send text messages to customers via RingCentral', 'Envía mensajes de texto a clientes vía RingCentral'),
      icon: MessageSquare,
      url: null,
      color: 'bg-emerald-500',
      category: 'communication',
      practice: {
        title: t('Practice: SMS Communication', 'Práctica: Comunicación por SMS'),
        scenario: t(
          'Practice sending professional text messages to customers.',
          'Practica enviando mensajes de texto profesionales a clientes.'
        ),
        steps: [
          t('Keep messages brief and professional', 'Mantén mensajes breves y profesionales'),
          t('Include your name and company', 'Incluye tu nombre y empresa'),
          t('State the purpose clearly', 'Establece el propósito claramente'),
          t('Provide contact options', 'Proporciona opciones de contacto'),
          t('Avoid sensitive information via text', 'Evita información sensible por texto')
        ]
      }
    }
  ];

  const categories = [
    { id: 'links', name: t('Application Links', 'Links de Solicitud'), icon: FileText },
    { id: 'portals', name: t('Partner Portals', 'Portales de Socios'), icon: Building },
    { id: 'documents', name: t('Documents', 'Documentos'), icon: ClipboardList },
    { id: 'verification', name: t('Verification', 'Verificación'), icon: Search },
    { id: 'communication', name: t('Communication', 'Comunicación'), icon: MessageSquare },
    { id: 'crm', name: 'CRM', icon: Users }
  ];

  const handleOpenTool = (tool) => {
    if (tool.url) {
      window.open(tool.url, '_blank');
      toast.success(t(`Opening ${tool.name}...`, `Abriendo ${tool.name}...`));
    } else {
      toast.info(t(
        `${tool.name} - Access through your installed application`,
        `${tool.name} - Accede a través de tu aplicación instalada`
      ));
    }
  };

  const handlePractice = (tool) => {
    setSelectedTool(tool);
    setPracticeDialog(true);
  };

  const handleCopyCredentials = (credentials) => {
    navigator.clipboard.writeText(`Usuario: ${credentials.username}\nContraseña: ${credentials.password}`);
    toast.success(t('Credentials copied!', '¡Credenciales copiadas!'));
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success(t('Link copied!', '¡Link copiado!'));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/training')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('Back', 'Volver')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Play className="h-6 w-6 text-primary" />
              {t('Customer Service Sandbox', 'Sandbox de Servicio al Cliente')}
            </h1>
            <p className="text-muted-foreground">
              {t(
                'Practice using the tools you will use daily as a customer service agent.',
                'Practica usando las herramientas que usarás diariamente como agente de servicio al cliente.'
              )}
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2 bg-amber-50">
          🎓 {t('Training Mode', 'Modo de Entrenamiento')}
        </Badge>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">
                {t('Training Environment', 'Ambiente de Entrenamiento')}
              </p>
              <p className="text-sm text-blue-700">
                {t(
                  'Some tools will open real portals. Practice navigating them but DO NOT submit real applications during training.',
                  'Algunas herramientas abrirán portales reales. Practica navegarlos pero NO envíes solicitudes reales durante el entrenamiento.'
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="tools">
            {t('Tools', 'Herramientas')}
          </TabsTrigger>
          <TabsTrigger value="scenarios">
            {t('Interactive Scenarios', 'Escenarios Interactivos')}
          </TabsTrigger>
          <TabsTrigger value="simulations">
            {t('Simulations', 'Simulaciones')}
          </TabsTrigger>
          <TabsTrigger value="certification">
            {t('Certification Quiz', 'Quiz de Certificación')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tools" className="space-y-6 mt-6">
          {categories.map((category) => {
            const categoryTools = tools.filter(t => t.category === category.id);
            if (categoryTools.length === 0) return null;
            
            const CategoryIcon = category.icon;
            
            return (
              <div key={category.id}>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <CategoryIcon className="h-5 w-5" />
                  {category.name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryTools.map((tool) => {
                    const ToolIcon = tool.icon;
                    return (
                      <Card key={tool.id} className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className={`p-2 rounded-lg ${tool.color} text-white`}>
                              <ToolIcon className="h-5 w-5" />
                            </div>
                            {tool.credentials && (
                              <Badge variant="outline" className="text-xs">
                                {t('Has Credentials', 'Tiene Credenciales')}
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-base mt-2">{tool.name}</CardTitle>
                          <CardDescription className="text-sm">
                            {tool.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {/* Credentials Section */}
                          {tool.credentials && (
                            <div className="mb-3 p-2 bg-muted rounded-lg">
                              <p className="text-xs font-medium mb-1">{t('Credentials:', 'Credenciales:')}</p>
                              <p className="text-xs">
                                <span className="text-muted-foreground">User:</span> {tool.credentials.username}
                              </p>
                              <p className="text-xs">
                                <span className="text-muted-foreground">Pass:</span> {tool.credentials.password}
                              </p>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="mt-1 h-6 text-xs"
                                onClick={() => handleCopyCredentials(tool.credentials)}
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                {t('Copy', 'Copiar')}
                              </Button>
                            </div>
                          )}
                          
                          {/* URL Section */}
                          {tool.url && (
                            <div className="mb-3">
                              <p className="text-xs text-muted-foreground truncate">{tool.url}</p>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 text-xs p-0"
                                onClick={() => handleCopyLink(tool.url)}
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                {t('Copy Link', 'Copiar Link')}
                              </Button>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            {tool.url && (
                              <Button 
                                size="sm" 
                                onClick={() => handleOpenTool(tool)}
                                className="flex-1"
                              >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                {t('Open', 'Abrir')}
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handlePractice(tool)}
                              className="flex-1"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              {t('Practice', 'Practicar')}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="scenarios" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('Practice Scenarios', 'Escenarios de Práctica')}</CardTitle>
              <CardDescription>
                {t(
                  'Click on any tool to see practice scenarios and step-by-step guides.',
                  'Haz clic en cualquier herramienta para ver escenarios de práctica y guías paso a paso.'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tools.map((tool) => {
                  const ToolIcon = tool.icon;
                  return (
                    <Button
                      key={tool.id}
                      variant="outline"
                      className="h-auto p-4 justify-start"
                      onClick={() => handlePractice(tool)}
                    >
                      <div className={`p-2 rounded-lg ${tool.color} text-white mr-3`}>
                        <ToolIcon className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{tool.practice.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {tool.practice.scenario}
                        </p>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Interactive Simulations Tab */}
        <TabsContent value="simulations" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="h-5 w-5 text-primary" />
                {t('Interactive Customer Simulations', 'Simulaciones Interactivas de Clientes')}
              </CardTitle>
              <CardDescription>
                {t(
                  'Practice handling real customer scenarios in a safe environment.',
                  'Practica manejando escenarios reales de clientes en un ambiente seguro.'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Simulation 1: New Customer Call */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      📞 {t('New Customer Inquiry', 'Consulta de Cliente Nuevo')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'A potential customer calls asking about truck financing. Guide them through the process.',
                        'Un cliente potencial llama preguntando sobre financiamiento de camiones. Guíalo a través del proceso.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{t('Difficulty: Easy', 'Dificultad: Fácil')}</Badge>
                      <Badge variant="outline">~10 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/new-customer')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 2: Document Collection */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      📄 {t('Document Collection', 'Recolección de Documentos')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'Customer has started an application. Help them gather all required documents.',
                        'El cliente ha iniciado una solicitud. Ayúdalo a reunir todos los documentos requeridos.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{t('Difficulty: Medium', 'Dificultad: Media')}</Badge>
                      <Badge variant="outline">~15 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/document-collection')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 3: Angry Customer */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      😤 {t('Handling Upset Customer', 'Manejando Cliente Molesto')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'A customer is frustrated about application delay. De-escalate and resolve.',
                        'Un cliente está frustrado por el retraso de su solicitud. Calma la situación y resuelve.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-orange-600 border-orange-300">{t('Difficulty: Hard', 'Dificultad: Difícil')}</Badge>
                      <Badge variant="outline">~20 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/angry-customer')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 4: Ban List Check */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      🚫 {t('Ban List Verification', 'Verificación de Lista de Prohibidos')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'New application received. Verify the customer is not on the ban list before processing.',
                        'Nueva solicitud recibida. Verifica que el cliente no esté en la lista de prohibidos antes de procesar.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{t('Difficulty: Easy', 'Dificultad: Fácil')}</Badge>
                      <Badge variant="outline">~5 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/ban-list')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 5: LOI Pricing */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      💰 {t('Generate LOI Quote', 'Generar Cotización LOI')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'Customer wants pricing options before committing. Use the LOI pricer to provide quotes.',
                        'El cliente quiere opciones de precio antes de comprometerse. Usa el LOI pricer para dar cotizaciones.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{t('Difficulty: Medium', 'Dificultad: Media')}</Badge>
                      <Badge variant="outline">~10 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/loi-pricing')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 6: CRM Documentation */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      📝 {t('CRM Documentation', 'Documentación en CRM')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'After a customer call, properly document all details in 360Portal.com.',
                        'Después de una llamada con cliente, documenta apropiadamente todos los detalles en 360Portal.com.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{t('Difficulty: Easy', 'Dificultad: Fácil')}</Badge>
                      <Badge variant="outline">~10 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/crm-documentation')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* ADVANCED SIMULATIONS - More Complex Scenarios */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  🎯 {t('Advanced Simulations', 'Simulaciones Avanzadas')}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t(
                    'These complex scenarios require critical thinking and mastery of all previous training.',
                    'Estos escenarios complejos requieren pensamiento crítico y dominio de todo el entrenamiento previo.'
                  )}
                </p>
              </div>

              {/* Simulation 7: Credit Denial Handling */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors border-orange-200 bg-orange-50/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      ❌ {t('Credit Denial Handling', 'Manejo de Negación de Crédito')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'A frustrated customer calls after receiving a credit denial. Handle professionally, explain options, and preserve the relationship.',
                        'Un cliente frustrado llama después de recibir una negación de crédito. Maneja profesionalmente, explica opciones y preserva la relación.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-orange-600 border-orange-300">{t('Difficulty: Hard', 'Dificultad: Difícil')}</Badge>
                      <Badge variant="outline">~15 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/credit-denial')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 8: Multi-Unit Deal */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors border-red-200 bg-red-50/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      🚛🚛🚛 {t('Complex Multi-Unit Deal', 'Trato Complejo de Múltiples Unidades')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'Handle a $600K fleet financing request with trade-ins, tight timelines, and unexpected complications. This requires advanced deal structuring skills.',
                        'Maneja una solicitud de financiamiento de flotilla de $600K con trade-ins, plazos ajustados y complicaciones inesperadas. Esto requiere habilidades avanzadas de estructuración de tratos.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-red-600 border-red-300">{t('Difficulty: Very Hard', 'Dificultad: Muy Difícil')}</Badge>
                      <Badge variant="outline">~20 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/multi-unit-deal')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 9: Fraud Detection */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors border-red-200 bg-red-50/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      🚨 {t('Fraud Red Flags', 'Banderas Rojas de Fraude')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'A suspicious customer wants to rush a deal with unusual payment structures. Identify fraud indicators and follow proper compliance procedures.',
                        'Un cliente sospechoso quiere apurar un trato con estructuras de pago inusuales. Identifica indicadores de fraude y sigue procedimientos de cumplimiento apropiados.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-red-600 border-red-300">{t('Difficulty: Very Hard', 'Dificultad: Muy Difícil')}</Badge>
                      <Badge variant="outline">~15 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/fraud-detection')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 10: Rate Negotiation */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors border-yellow-200 bg-yellow-50/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      📉 {t('Rate Negotiation', 'Negociación de Tasas')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'Customer pushes back on rate and threatens to go elsewhere. Navigate rate discussions while preserving the deal and maintaining profitability.',
                        'El cliente presiona por la tasa y amenaza con ir a otro lado. Navega discusiones de tasa mientras preservas el trato y mantienes rentabilidad.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-yellow-600 border-yellow-300">{t('Difficulty: Medium-Hard', 'Dificultad: Media-Difícil')}</Badge>
                      <Badge variant="outline">~12 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/rate-negotiation')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>

              {/* Simulation 11: Competitor Comparison */}
              <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors border-blue-200 bg-blue-50/30">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      ⚖️ {t('Competitor Comparison', 'Comparación con Competidores')}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(
                        'Customer is shopping multiple lenders and comparing offers. Differentiate your value proposition without badmouthing competition.',
                        'El cliente está buscando múltiples prestamistas y comparando ofertas. Diferencia tu propuesta de valor sin hablar mal de la competencia.'
                      )}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{t('Difficulty: Medium', 'Dificultad: Media')}</Badge>
                      <Badge variant="outline">~12 min</Badge>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/training/simulation/competitor-comparison')}>
                    {t('Start', 'Iniciar')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Certification Quiz Tab */}
        <TabsContent value="certification" className="space-y-6 mt-6">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                🏆 {t('Sandbox Certification Quiz', 'Quiz de Certificación del Sandbox')}
              </CardTitle>
              <CardDescription>
                {t(
                  'Complete this quiz to demonstrate your knowledge of all tools and processes.',
                  'Completa este quiz para demostrar tu conocimiento de todas las herramientas y procesos.'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quiz Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-white/70 rounded-lg border">
                  <p className="text-sm text-muted-foreground">{t('Questions', 'Preguntas')}</p>
                  <p className="text-2xl font-bold">25</p>
                </div>
                <div className="p-4 bg-white/70 rounded-lg border">
                  <p className="text-sm text-muted-foreground">{t('Passing Score', 'Puntaje Mínimo')}</p>
                  <p className="text-2xl font-bold">80%</p>
                </div>
                <div className="p-4 bg-white/70 rounded-lg border">
                  <p className="text-sm text-muted-foreground">{t('Time Limit', 'Tiempo Límite')}</p>
                  <p className="text-2xl font-bold">30 min</p>
                </div>
              </div>

              {/* Topics Covered */}
              <div>
                <h4 className="font-semibold mb-3">{t('Topics Covered:', 'Temas Cubiertos:')}</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {t('Customer Application Process', 'Proceso de Solicitud del Cliente')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {t('EFAI Portal Navigation', 'Navegación del Portal EFAI')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {t('Document Requirements', 'Requisitos de Documentos')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {t('Ban List Verification', 'Verificación de Lista de Prohibidos')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {t('LOI Pricer Usage', 'Uso del LOI Pricer')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {t('CRM Documentation', 'Documentación en CRM')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {t('Communication Best Practices', 'Mejores Prácticas de Comunicación')}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {t('Fleet Truck Verification', 'Verificación de Camiones de Flotilla')}
                  </li>
                </ul>
              </div>

              {/* Start Quiz Button */}
              <div className="flex flex-col items-center gap-4 pt-4">
                <Button 
                  size="lg" 
                  className="w-full md:w-auto px-8"
                  onClick={() => navigate('/training/sandbox/quiz')}
                >
                  <Play className="h-5 w-5 mr-2" />
                  {t('Start Certification Quiz', 'Iniciar Quiz de Certificación')}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  {t(
                    'You must pass this quiz before being promoted to production.',
                    'Debes aprobar este quiz antes de ser promovido a producción.'
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Practice Dialog */}
      <Dialog open={practiceDialog} onOpenChange={setPracticeDialog}>
        <DialogContent className="max-w-2xl">
          {selectedTool && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${selectedTool.color} text-white`}>
                    <selectedTool.icon className="h-5 w-5" />
                  </div>
                  {selectedTool.practice.title}
                </DialogTitle>
                <DialogDescription>
                  {selectedTool.practice.scenario}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Warning if exists */}
                {selectedTool.practice.warning && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                    <p className="text-sm text-red-800 font-medium">
                      {selectedTool.practice.warning}
                    </p>
                  </div>
                )}
                
                {/* Steps */}
                <div>
                  <h4 className="font-medium mb-3">{t('Steps to Follow:', 'Pasos a Seguir:')}</h4>
                  <ol className="space-y-2">
                    {selectedTool.practice.steps.map((step, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium flex items-center justify-center">
                          {index + 1}
                        </span>
                        <span className="text-sm">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                
                {/* Email Templates if exists */}
                {selectedTool.practice.templates && (
                  <div>
                    <h4 className="font-medium mb-3">{t('Email Templates:', 'Plantillas de Email:')}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedTool.practice.templates.map((template, index) => (
                        <Button 
                          key={index} 
                          variant="outline" 
                          size="sm"
                          className="justify-start"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          {template.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Credentials if exists */}
                {selectedTool.credentials && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      🔐 {t('Login Credentials', 'Credenciales de Acceso')}
                    </h4>
                    <p className="text-sm"><strong>Username:</strong> {selectedTool.credentials.username}</p>
                    <p className="text-sm"><strong>Password:</strong> {selectedTool.credentials.password}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => handleCopyCredentials(selectedTool.credentials)}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      {t('Copy Credentials', 'Copiar Credenciales')}
                    </Button>
                  </div>
                )}
                
                {/* Tool URL */}
                {selectedTool.url && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">{t('Tool URL:', 'URL de la Herramienta:')}</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-white px-2 py-1 rounded flex-1 truncate">
                        {selectedTool.url}
                      </code>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleCopyLink(selectedTool.url)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setPracticeDialog(false)}>
                  {t('Close', 'Cerrar')}
                </Button>
                {selectedTool.url && (
                  <Button onClick={() => handleOpenTool(selectedTool)}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t('Open Tool', 'Abrir Herramienta')}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
